import { useAppDispatch } from '@/app/hooks';
import { resetAppState } from '@/app/store';
import { AccountContext } from '@/hooks/useAccount';
import { claimUnassignedData, clearLegacyAccountCache } from '@/lib/account-data';
import { AccountLoadError, accountRedirect, fetchAccount } from '@/lib/account-session';
import type { PqUser } from '@/lib/pq-client';
import { clearAllPendingConfirmations } from '@/lib/reactivation';
import { Button, Group, Stack, Text, Title } from '@mantine/core';
import { AuthKitError, useAuth } from '@workos-inc/authkit-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import AuthLoading from '@/components/AuthLoading';

export default function AccountGate({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    return user ? <AccountSession key={user.id} userId={user.id}>{children}</AccountSession> : null;
}

function AccountSession({ userId, children }: { userId: string; children: ReactNode }) {
    const { getAccessToken, signIn, signOut: authSignOut } = useAuth();
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const [account, setAccount] = useState<PqUser | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const mounted = useRef(false);
    const generation = useRef(0);
    const request = useRef<AbortController | null>(null);
    const actionPending = useRef(false);
    const tokenGetter = useRef(getAccessToken);

    useEffect(() => { tokenGetter.current = getAccessToken; }, [getAccessToken]);

    const refreshAccount = useCallback(async () => {
        const current = ++generation.current;
        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        const next = await fetchAccount(() => tokenGetter.current(), controller.signal);
        if (mounted.current && generation.current === current) setAccount(next);
        return next;
    }, []);

    const load = useCallback(async () => {
        setError(null);
        setBusy(true);
        try {
            await clearLegacyAccountCache().catch(() => undefined);
            await claimUnassignedData(userId);
            if (!mounted.current) return;
            await refreshAccount();
        } catch (cause) {
            if (mounted.current && !(cause instanceof DOMException && cause.name === 'AbortError')) {
                setError(cause instanceof AccountLoadError ? cause : new Error('Unable to load your account. Check your connection and try again.'));
            }
        } finally {
            if (mounted.current) setBusy(false);
        }
    }, [refreshAccount, userId]);

    useEffect(() => {
        mounted.current = true;
        dispatch(resetAppState());
        void load();
        return () => {
            mounted.current = false;
            request.current?.abort();
        };
    }, [dispatch, load]);

    const signOut = useCallback(async () => {
        setAccount(null);
        setError(null);
        ++generation.current;
        request.current?.abort();
        try {
            await clearLegacyAccountCache().catch(() => undefined);
            clearAllPendingConfirmations();
            dispatch(resetAppState());
            await authSignOut();
        } catch (cause) {
            // AuthKit throws NoSessionError when its access token is already gone (an expired
            // or revoked session), so there is no server session left to end. Reloading
            // re-initializes AuthKit signed out, which shows the login page.
            if (cause instanceof AuthKitError) {
                window.location.replace('/');
                return;
            }
            if (mounted.current) setError(new AccountLoadError('Unable to sign out. Please try again or sign in again.', true));
            throw cause;
        }
    }, [authSignOut, dispatch]);

    const runAction = async (action: () => Promise<unknown> | void) => {
        if (actionPending.current) return;
        actionPending.current = true;
        setBusy(true);
        setActionError(null);
        try {
            await action();
        } catch {
            if (mounted.current) setActionError('Unable to complete this action. Please try again.');
        } finally {
            actionPending.current = false;
            if (mounted.current) setBusy(false);
        }
    };

    if (!account) {
        if (!error) return <AuthLoading message="Loading your account..." />;
        return (
            <Stack align="center" justify="center" h="100%" p="lg" gap="md">
                <Title order={2} c="white">Account unavailable</Title>
                <Text role="alert" ta="center" c="white">{error.message}</Text>
                {actionError && <Text c="red" role="alert">{actionError}</Text>}
                <Group>
                    <Button color="pq-red" loading={busy} onClick={() => void runAction(
                        error instanceof AccountLoadError && error.requiresSignIn ? () => signIn() : load,
                    )}>{error instanceof AccountLoadError && error.requiresSignIn ? 'Sign in again' : 'Try again'}</Button>
                    <Button variant="subtle" color="gray.0" disabled={busy} onClick={() => void runAction(signOut)}>Sign out</Button>
                </Group>
            </Stack>
        );
    }

    const redirect = accountRedirect(account, pathname);
    if (redirect) return <Navigate to={redirect} replace />;

    return (
        <AccountContext.Provider value={{ userId, account, refreshAccount, signOut }}>
            {/* Inactive-account pages (billing, support) show their own Sign out button. */}
            {children}
        </AccountContext.Provider>
    );
}
